import type { Background, Section, SpacingValue } from "@emkit/builder-core";
import { useEditor } from "../../store.js";
import { readAtBreakpoint, writeAtBreakpoint } from "../../utils/responsive.js";
import { AdvancedFields } from "./AdvancedFields.js";
import { ColorField, NumberField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";

interface Props {
  section: Section;
}

const BG_KINDS = ["none", "color", "image", "video"] as const;
type BgKind = (typeof BG_KINDS)[number];

const MAX_WIDTH_PRESETS = ["640", "780", "900", "1100", "1200", "full"] as const;

const BP_LABEL: Record<string, string> = {
  desktop: "desktop",
  tablet: "tablet",
  mobile: "mobile",
};

const LAYOUT_PRESETS: {
  id: string;
  label: string;
  spans: number[];
  icon: string;
}[] = [
  { id: "1", label: "1 column", spans: [12], icon: "▮" },
  { id: "2-eq", label: "2 equal (6/6)", spans: [6, 6], icon: "▮▮" },
  { id: "3-eq", label: "3 equal (4/4/4)", spans: [4, 4, 4], icon: "▮▮▮" },
  { id: "4-eq", label: "4 equal (3×4)", spans: [3, 3, 3, 3], icon: "▮▮▮▮" },
  { id: "1-2", label: "Narrow + wide (4/8)", spans: [4, 8], icon: "▮▭" },
  { id: "2-1", label: "Wide + narrow (8/4)", spans: [8, 4], icon: "▭▮" },
];

export function SectionInspector({ section }: Props) {
  const update = useEditor((s) => s.updateSection);
  const remove = useEditor((s) => s.removeSection);
  const addColumn = useEditor((s) => s.addColumn);
  const setColumnLayout = useEditor((s) => s.setColumnLayout);
  const moveSection = useEditor((s) => s.moveSection);
  const duplicateSection = useEditor((s) => s.duplicateSection);
  const sections = useEditor((s) => s.page.sections);
  const sectionCount = sections.length;
  const sectionIndex = sections.findIndex((s) => s.id === section.id);
  const canMoveUp = sectionIndex > 0;
  const canMoveDown = sectionIndex < sectionCount - 1;
  const breakpoint = useEditor((s) => s.breakpoint);

  const bg = section.background ?? { kind: "none" };
  const padding = readAtBreakpoint<SpacingValue>(section.padding, breakpoint) ?? {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  const maxWidth = readAtBreakpoint(section.maxWidth, breakpoint);
  const maxWidthStr =
    maxWidth === undefined
      ? "1200"
      : typeof maxWidth === "number"
        ? String(maxWidth)
        : maxWidth;

  const setBgKind = (kind: BgKind) => {
    let next: Background;
    if (kind === "none") next = { kind: "none" };
    else if (kind === "color") next = { kind: "color", color: "#ffffff" };
    else if (kind === "image")
      next = { kind: "image", url: "https://placehold.co/1200x600", size: "cover" };
    else next = { kind: "video", url: "" };
    update(section.id, { background: next });
  };

  const setPad = (side: keyof SpacingValue, value: number) => {
    const nextPadding = writeAtBreakpoint<SpacingValue>(section.padding, breakpoint, {
      ...padding,
      [side]: value,
    });
    update(section.id, { padding: nextPadding });
  };

  const setMaxWidth = (value: string) => {
    const next: number | "full" = value === "full" ? "full" : Number.parseInt(value, 10);
    if (typeof next === "number" && Number.isNaN(next)) return;
    const nextMw = writeAtBreakpoint<number | "full">(section.maxWidth, breakpoint, next);
    update(section.id, { maxWidth: nextMw });
  };

  return (
    <aside className="emke-inspector">
      <div className="emke-section-title">
        Section · <span style={{ color: "var(--accent)" }}>{section.id}</span>
      </div>

      <div style={{ padding: "0 16px 8px", display: "flex", gap: 4 }}>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveSection(section.id, "up")}
          disabled={!canMoveUp}
          style={{ flex: 1, opacity: canMoveUp ? 1 : 0.4 }}
          title="Move section up"
        >
          ↑
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => moveSection(section.id, "down")}
          disabled={!canMoveDown}
          style={{ flex: 1, opacity: canMoveDown ? 1 : 0.4 }}
          title="Move section down"
        >
          ↓
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={() => duplicateSection(section.id)}
          style={{ flex: 1 }}
          title="Duplicate section"
        >
          ⎘
        </button>
      </div>

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Background
      </div>
      <SelectField
        label="Type"
        value={bg.kind as BgKind}
        options={BG_KINDS}
        onChange={setBgKind}
      />

      {bg.kind === "color" && (
        <ColorField
          label="Color"
          value={bg.color}
          onChange={(v) =>
            update(section.id, { background: { kind: "color", color: v } })
          }
        />
      )}
      {bg.kind === "image" && (
        <>
          <MediaField
            label="Image URL"
            value={bg.url}
            onChange={(v) =>
              update(section.id, { background: { ...bg, url: v } as Background })
            }
          />
          <SelectField
            label="Size"
            value={bg.size ?? "cover"}
            options={["cover", "contain"] as const}
            onChange={(v) =>
              update(section.id, { background: { ...bg, size: v } as Background })
            }
          />
        </>
      )}
      {bg.kind === "video" && (
        <>
          <MediaField
            label="Video URL (mp4)"
            value={bg.url}
            accept="video/*"
            onChange={(v) =>
              update(section.id, { background: { ...bg, url: v } as Background })
            }
          />
          <MediaField
            label="Poster URL (optional)"
            value={bg.poster ?? ""}
            onChange={(v) =>
              update(section.id, { background: { ...bg, poster: v } as Background })
            }
          />
        </>
      )}

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Padding ({BP_LABEL[breakpoint]})
      </div>
      <div className="emke-inspector-grid-2" style={{ padding: "0 16px" }}>
        <NumberField
          label="Top"
          value={padding.top ?? 0}
          min={0}
          max={400}
          onChange={(v) => setPad("top", v)}
        />
        <NumberField
          label="Bottom"
          value={padding.bottom ?? 0}
          min={0}
          max={400}
          onChange={(v) => setPad("bottom", v)}
        />
      </div>
      <div className="emke-inspector-grid-2" style={{ padding: "0 16px" }}>
        <NumberField
          label="Left"
          value={padding.left ?? 0}
          min={0}
          max={400}
          onChange={(v) => setPad("left", v)}
        />
        <NumberField
          label="Right"
          value={padding.right ?? 0}
          min={0}
          max={400}
          onChange={(v) => setPad("right", v)}
        />
      </div>

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Layout ({BP_LABEL[breakpoint]})
      </div>
      <SelectField
        label="Max width"
        value={maxWidthStr as (typeof MAX_WIDTH_PRESETS)[number]}
        options={MAX_WIDTH_PRESETS}
        onChange={setMaxWidth}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Column layout
      </div>
      <div
        style={{
          padding: "0 16px",
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 6,
        }}
      >
        {LAYOUT_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="emke-btn"
            onClick={() => setColumnLayout(section.id, p.spans)}
            title={p.label}
            style={{
              padding: "8px 4px",
              fontSize: 11,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                letterSpacing: -1,
                fontSize: 14,
              }}
            >
              {p.icon}
            </span>
            <span style={{ color: "var(--fg-muted)" }}>{p.spans.join("/")}</span>
          </button>
        ))}
      </div>

      <div
        style={{
          padding: "12px 16px 0",
          display: "flex",
          gap: 6,
          flexDirection: "column",
        }}
      >
        <button
          type="button"
          className="emke-btn"
          onClick={() => addColumn(section.id)}
          title="Add an empty column to this section"
        >
          + Add column
        </button>
      </div>

      <AdvancedFields
        value={section}
        onChange={(patch) => update(section.id, patch)}
      />

      <div
        style={{
          padding: "12px 16px 16px",
          borderTop: "1px solid var(--border)",
          marginTop: 16,
        }}
      >
        <button
          type="button"
          className="emke-btn is-danger"
          onClick={() => remove(section.id)}
          disabled={sectionCount <= 1}
          title={
            sectionCount <= 1 ? "Can't delete the last section" : "Delete this section"
          }
          style={{ width: "100%", opacity: sectionCount <= 1 ? 0.4 : 1 }}
        >
          Delete section
        </button>
      </div>
    </aside>
  );
}
